import { useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'

import { supportService } from '@/app/services/supportService'

import { useLoadMoreOnScroll } from './useLoadMoreOnScroll'
import {
  PersonTransactionFilter,
  PersonTransactionRole,
} from '@/app/services/supportService/listPersonTransactions'

const PAGE_SIZE = 15

interface UsePersonTransactionsParams {
  personId: string
  role: PersonTransactionRole
  filter: PersonTransactionFilter | null
}

export function usePersonTransactions({
  personId,
  role,
  filter,
}: UsePersonTransactionsParams) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['supportPersonTransactions', personId, role, filter],
      queryFn: ({ pageParam }) =>
        supportService.listPersonTransactions({
          personId,
          role,
          filter: filter as PersonTransactionFilter,
          skip: pageParam,
          limit: PAGE_SIZE,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        const nextSkip = lastPage.pagination.skip + lastPage.pagination.limit
        return nextSkip >= lastPage.pagination.total ? undefined : nextSkip
      },
      enabled: !!filter,
    })

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.transactions) ?? [],
    [data],
  )

  const total = data?.pages[0]?.pagination.total ?? 0

  useLoadMoreOnScroll({
    anchorRef: sentinelRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: () => fetchNextPage(),
  })

  return {
    items,
    total,
    isLoading: !!filter && isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
  }
}
